<script setup>
import { PhxTabPanel, PhxTabs } from '@phx-styleguides/iwc';
import { useStore } from 'vuex';
import { getTabComponent, TABS_ORDER } from '@maison/helpers/search-helper';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSearchPanelResults } from '@phoenix/composables/components/search/searchPanelResults';

const store = useStore();
const { t } = useI18n();
const { onTabChange } = useSearchPanelResults();

const idPrefix = 'search-panel-results';
const tabs = TABS_ORDER;

const tabsTeleportTargetRef = ref(null);

const currentTab = computed(() => store.state.search.currentTab);
const currentTabId = computed(() => getTabId(currentTab.value));
const isLoading = computed(() => store.state.search.isLoading);

const getTabId = (tab) => `${idPrefix}-${tab}`;

const getTabComponentId = (tab) => `${getTabId(tab)}-component`;

const getTabTotal = (tab) => store.state.search.tabs[tab]?.total;

const getTabLabel = (tab) => `${t(`Search.tab.${tab}`)} (${getTabTotal(tab)})`;

const isTabDisabled = (tab) => {
  return store.state.search.tabs[tab].total === 0;
};

watch(
  () => isLoading.value,
  async (newValue) => {
    if (newValue) {
      return;
    }

    const tabs = store.state.search.tabs;
    const tabNames = Object.keys(tabs);
    const firstNonEmptyTab = tabNames.find((tab) => tabs[tab]?.data?.length > 0);
    const firstNonEmptyTabId = getTabId(firstNonEmptyTab);

    if (firstNonEmptyTabId !== currentTabId.value) {
      await store.dispatch('search/changeCurrentTab', firstNonEmptyTabId);
    }
  },
);
</script>

<template>
  <div class="search-panel__tabs-wrapper">
    <phx-tabs
      v-cloak
      :id="`${idPrefix}-tabs`"
      class="tabs-group--with-indicator"
      theme="light"
      layout="start"
      :value="currentTabId"
      :indicator-thickness="1"
      :teleport-target="tabsTeleportTargetRef"
      @change="onTabChange"
    >
      <phx-tab-panel
        v-for="tab in tabs"
        :id="getTabId(tab)"
        :key="tab"
        :label="getTabLabel(tab)"
        :disabled="isTabDisabled(tab)"
      >
        <component :is="getTabComponent(tab)" :unique-id="getTabComponentId(tab)" />
      </phx-tab-panel>
    </phx-tabs>

    <div id="teleport-target-search-panel-filters" />
  </div>

  <div ref="tabsTeleportTargetRef" class="search-panel__tabs-content"></div>
</template>
